/**
 * Admin Portal Main Container (T038, T014, T026 - US1/US2)
 * Main container with tab navigation and resource editor routes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Routes, Route } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building,
  Building2,
  UserCheck,
  FileText,
  BarChart3,
  Activity,
  Settings,
  Database,
  HeartHandshake,
  SlidersHorizontal,
} from 'lucide-react';
import { Tabs, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs';
import { ScrollableTabsList } from '@/shared/components/ui/scrollable-tabs-list';
import { useAdminAuth } from './hooks/useAdminAuth';
import { AdminErrorBoundary } from './components/AdminErrorBoundary';
import { OverviewTab } from './components/OverviewTab';
import { UsersTab } from './components/UsersTab';
import { ProvidersTab } from './components/ProvidersTab';
import { AdvisorsTab } from './components/AdvisorsTab';
import { AnalyticsTab } from './components/AnalyticsTab';
import { EngagementMetricsTab } from './components/EngagementMetricsTab';
import { SettingsTab } from './components/SettingsTab';
import { PartnerCompaniesTab } from './components/PartnerCompaniesTab';
import { ResourcesTab } from './components/ResourcesTab';
import { ResourceEditorPage } from './components/ResourceEditorPage';
import { VectorDbTab } from './vector-db/components/VectorDbTab';
import { ConciergeCasesTab } from './components/care-concierge/ConciergeCasesTab';
import { NavigatorTuningTab } from './navigator-tuning/components/NavigatorTuningTab';

type TabValue =
  | 'overview'
  | 'users'
  | 'providers'
  | 'advisors'
  | 'partners'
  | 'resources'
  | 'analytics'
  | 'engagement-metrics'
  | 'settings'
  | 'vector-db'
  | 'concierge-cases'
  | 'navigator-tuning';

export function AdminPortal() {
  const navigate = useNavigate();
  const params = useParams();
  const tab = params['*']?.split('/')[0] || undefined;
  const location = useLocation();
  const { isLoading, canViewBasicMetrics, canViewAllMetrics } = useAdminAuth();
  const canSeeEngagementMetrics = canViewBasicMetrics && canViewAllMetrics;
  const [activeTab, setActiveTab] = useState<TabValue>('overview');

  // Detect resource editor sub-routes
  const isEditorRoute =
    location.pathname.includes('/admin/resources/create') ||
    /\/admin\/resources\/[^/]+\/edit/.test(location.pathname);

  // Update active tab based on URL parameter
  useEffect(() => {
    if (isLoading) return; // permissions still resolving — don't redirect on a stale false
    if (tab && isValidTab(tab)) {
      if (
        (tab === 'engagement-metrics' && !canSeeEngagementMetrics) ||
        (tab === 'navigator-tuning' && !canViewAllMetrics)
      ) {
        setActiveTab('overview');
        navigate('/admin', { replace: true });
        return;
      }
      setActiveTab(tab as TabValue);
    } else {
      setActiveTab('overview');
    }
  }, [tab, isLoading, canSeeEngagementMetrics, canViewAllMetrics, navigate]);

  // Resource editor sub-routes (full-page, no tabs)
  if (isEditorRoute) {
    return (
      <Routes>
        <Route path="resources/create" element={<ResourceEditorPage />} />
        <Route path="resources/:id/edit" element={<ResourceEditorPage />} />
      </Routes>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#026857] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue);
    navigate(`/admin/${value === 'overview' ? '' : value}`);
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="admin-portal-container"
    >
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Tab Navigation */}
          <ScrollableTabsList containerClassName="mb-6" className="w-full justify-start border-b bg-transparent p-0 h-auto">
            <TabsTrigger
              value="overview"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>

            <TabsTrigger
              value="users"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <Users className="h-4 w-4" />
              <span>Users</span>
            </TabsTrigger>

            <TabsTrigger
              value="providers"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <Building className="h-4 w-4" />
              <span>Providers</span>
            </TabsTrigger>

            <TabsTrigger
              value="advisors"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <UserCheck className="h-4 w-4" />
              <span>Advisors</span>
            </TabsTrigger>

            <TabsTrigger
              value="partners"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <Building2 className="h-4 w-4" />
              <span>Partners</span>
            </TabsTrigger>

            <TabsTrigger
              value="concierge-cases"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <HeartHandshake className="h-4 w-4" />
              <span>Concierge</span>
            </TabsTrigger>

            <TabsTrigger
              value="resources"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <FileText className="h-4 w-4" />
              <span>Resources</span>
            </TabsTrigger>

            <TabsTrigger
              value="analytics"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <BarChart3 className="h-4 w-4" />
              <span>Analytics</span>
            </TabsTrigger>

            {canViewAllMetrics && (
              <TabsTrigger
                value="navigator-tuning"
                className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Navigator Tuning</span>
              </TabsTrigger>
            )}

            {canSeeEngagementMetrics && (
              <TabsTrigger
                value="engagement-metrics"
                className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
              >
                <Activity className="h-4 w-4" />
                <span>Engagement Metrics</span>
              </TabsTrigger>
            )}

              <TabsTrigger
              value="vector-db"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <Database className="h-4 w-4" />
              <span>Manage Vector DB</span>
            </TabsTrigger>

            <TabsTrigger
              value="settings"
              className="flex items-center gap-2 px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-[#026857] rounded-none"
            >
              <Settings className="h-4 w-4" />
              <span>Settings</span>
            </TabsTrigger>


          </ScrollableTabsList>

          {/* Tab Content - Each wrapped in error boundary */}
          <TabsContent value="overview" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Dashboard Error"
              fallbackMessage="Failed to load the overview dashboard. Please try refreshing the page."
            >
              <OverviewTab />
            </AdminErrorBoundary>
          </TabsContent>
          
          <TabsContent value="users" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="User Management Error"
              fallbackMessage="Failed to load user management interface. Please try refreshing the page."
            >
              <UsersTab />
            </AdminErrorBoundary>
          </TabsContent>
          
          <TabsContent value="providers" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Provider Management Error"
              fallbackMessage="Failed to load provider management interface. Please try refreshing the page."
            >
              <ProvidersTab />
            </AdminErrorBoundary>
          </TabsContent>
          
          <TabsContent value="advisors" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Advisor Management Error"
              fallbackMessage="Failed to load advisor management interface. Please try refreshing the page."
            >
              <AdvisorsTab />
            </AdminErrorBoundary>
          </TabsContent>
          
          <TabsContent value="concierge-cases" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Concierge Cases Error"
              fallbackMessage="Failed to load concierge cases. Please try refreshing the page."
            >
              <ConciergeCasesTab />
            </AdminErrorBoundary>
          </TabsContent>

          <TabsContent value="partners" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Partner Companies Error"
              fallbackMessage="Failed to load partner companies interface. Please try refreshing the page."
            >
              <PartnerCompaniesTab />
            </AdminErrorBoundary>
          </TabsContent>
          
          <TabsContent value="resources" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Resources Error"
              fallbackMessage="Failed to load resources interface. Please try refreshing the page."
            >
              <ResourcesTab />
            </AdminErrorBoundary>
          </TabsContent>

          <TabsContent value="analytics" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Analytics Error"
              fallbackMessage="Failed to load analytics interface. Please try refreshing the page."
            >
              <AnalyticsTab />
            </AdminErrorBoundary>
          </TabsContent>

          {canViewAllMetrics && (
            <TabsContent value="navigator-tuning" className="mt-0">
              <AdminErrorBoundary
                fallbackTitle="Navigator Tuning Error"
                fallbackMessage="Failed to load Navigator Tuning. Please try refreshing the page."
              >
                <NavigatorTuningTab />
              </AdminErrorBoundary>
            </TabsContent>
          )}

          {canSeeEngagementMetrics && (
            <TabsContent value="engagement-metrics" className="mt-0">
              <AdminErrorBoundary
                fallbackTitle="Engagement Metrics Error"
                fallbackMessage="Failed to load engagement metrics interface. Please try refreshing the page."
              >
                <EngagementMetricsTab />
              </AdminErrorBoundary>
            </TabsContent>
          )}

          <TabsContent value="settings" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Settings Error"
              fallbackMessage="Failed to load settings interface. Please try refreshing the page."
            >
              <SettingsTab />
            </AdminErrorBoundary>
          </TabsContent>

          <TabsContent value="vector-db" className="mt-0">
            <AdminErrorBoundary
              fallbackTitle="Vector DB Error"
              fallbackMessage="Failed to load Vector DB management interface. Please try refreshing the page."
            >
              <VectorDbTab />
            </AdminErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Helper function to validate tab names
function isValidTab(tab: string): boolean {
  const validTabs: TabValue[] = [
    'overview',
    'users',
    'providers',
    'advisors',
    'partners',
    'resources',
    'analytics',
    'engagement-metrics',
    'settings',
    'vector-db',
    // Kept in step with TabValue above by hand: this list is a separate literal, and a tab
    // missing from it is silently redirected to Overview rather than failing.
    'concierge-cases',
    'navigator-tuning',
  ];
  return validTabs.includes(tab as TabValue);
}
